import { redirect } from '@tanstack/react-router'
import { useAuthStore } from '../stores/auth'
import { AccessCategory } from '@/api/main/types.gen'

// Check if user is authenticated and token is not expired
export const requireAuth = () => {
  const authStore = useAuthStore.getState()
  // isAuthenticated() already checks token expiration internally
  if (!authStore.isAuthenticated()) {
    throw redirect({
      to: '/login',
      search: {
        redirect: window.location.pathname,
      },
    })
  }
}

export const requirePermission = (resource: AccessCategory, action?: 'read' | 'write') => {
  const authStore = useAuthStore.getState()

  // Check authentication (isAuthenticated already checks token expiration internally)
  if (!authStore.isAuthenticated()) {
    throw redirect({
      to: '/login',
      search: {
        redirect: window.location.pathname,
      },
    })
  }

  // Check if permissions are loaded - if not, we can't verify access
  // This handles the case where user is authenticated but permissions haven't been fetched yet
  const { userPermissions } = authStore
  if (!userPermissions) {
    // Permissions not loaded - redirect to login to force re-authentication and permission fetch
    throw redirect({
      to: '/login',
      search: {
        redirect: window.location.pathname,
      },
    })
  }

  const hasAccess = authStore.hasPermission(resource, action)

  if (!hasAccess) {
    throw redirect({
      to: '/',
      search: {
        error: 'insufficient_permissions',
      },
    })
  }
}

export const requireAnyPermission = (resources: AccessCategory[]) => {
  const authStore = useAuthStore.getState()

  // Check authentication (isAuthenticated already checks token expiration internally)
  if (!authStore.isAuthenticated()) {
    throw redirect({
      to: '/login',
      search: {
        redirect: window.location.pathname,
      },
    })
  }

  // Check if permissions are loaded - if not, we can't verify access
  // This handles the case where user is authenticated but permissions haven't been fetched yet
  const { userPermissions } = authStore
  if (!userPermissions) {
    // Permissions not loaded - redirect to login to force re-authentication and permission fetch
    throw redirect({
      to: '/login',
      search: {
        redirect: window.location.pathname,
      },
    })
  }

  // Check if user has any of the required permissions
  const hasAccess = resources.some(resource => authStore.hasAnyPermission(resource))

  if (!hasAccess) {
    throw redirect({
      to: '/',
      search: {
        error: 'insufficient_permissions',
      },
    })
  }
}

// Redirect to dashboard if already authenticated
export const requireGuest = ({ search }: { search: Record<string, unknown> }) => {
  const authStore = useAuthStore.getState()

  // isAuthenticated() already checks token expiration internally
  if (authStore.isAuthenticated()) {
    const redirectUrl = getRedirectUrl(search)
    throw redirect({
      to: redirectUrl,
    })
  }
}

export const getRedirectUrl = (search: Record<string, unknown>) => {
  return (search.redirect as string) || '/'
}
